
import { S3_UPLOAD_BASE_URL } from "../Config/index.js";
import axios from "axios";
import { API_URL } from "../Config/index.js";
import { emitEvent } from "../MasterData/emitter";
import { getAuthTokenPayload } from "../Utils/index.js";

const getStoredIds = () => ({
  orgId: getAuthTokenPayload()?.clientId ?? "",
  userId: getAuthTokenPayload()?.userId ?? "",
});

// Helper function to get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

const handleSessionTimeout = (status, data) => {
  if (status === 401 && data?.sessionTimeout) {
    emitEvent("session-timeout", data);
    return true;
  }
  return false;
};

export const getPresignedUrl = async (fileName, contentType) => {
  const token = getAuthToken();
  const url = `${S3_UPLOAD_BASE_URL}/s3-upload?fileName=${encodeURIComponent(
    fileName
  )}&contentType=${encodeURIComponent(contentType)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `${token}` } : {}),
    },
  });

  const result = await response.json().catch(() => ({}));

  if (handleSessionTimeout(response.status, result)) {
    return {
      success: false,
      data: [],
      message: result.uiMsg || result.message || "Session timeout",
    };
  }

  if (!response.ok) {
    const errorMessage = result.message || result.error || result.uiMsg || 
      `Failed to get upload URL (${response.status}: ${response.statusText})`;
    console.error("getPresignedUrl error:", {
      status: response.status,
      statusText: response.statusText,
      fileName,
      contentType,
      result
    });
    throw new Error(errorMessage);
  }

  return result;
};

export const uploadFileToS3 = async (uploadUrl, file) => {
  console.log("Uploading file to S3 with URL:", uploadUrl);
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to upload file to S3");
  }

  return true;
};

export const getMarketingAssetPresignedUrl = async (
  fileName,
  folderPath,
  orgId,
  userId
) => {
  try {
    const stored = getStoredIds();
    const effectiveOrgId = orgId || stored.orgId;
    const effectiveUserId = userId || stored.userId;
    const token = getAuthToken();
    const url = `${API_URL}/file-upload/${effectiveOrgId}/${effectiveUserId}`;
    const response = await axios.post(
      url,
      { folderPath, fileName },
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        }
      }
    );
    if (handleSessionTimeout(response.status, response.data)) {
      return {
        success: false,
        data: [],
        message:
          response.data?.uiMsg || response.data?.message || "Session timeout",
      };
    }
    return response.data;
  } catch (error) {
    if (error?.response) {
      if (handleSessionTimeout(error.response.status, error.response.data)) {
        return {
          success: false,
          data: [],
          message:
            error.response.data?.uiMsg ||
            error.response.data?.message ||
            "Session timeout",
        };
      }
    }
    throw error;
  }
};

export const uploadMarketingAssetToS3 = async (uploadUrl, file, extraHeaders = {}) => {
  console.log("Uploading file to S3 with URL:", uploadUrl);
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...extraHeaders,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "");
    throw new Error(
      `S3 upload failed (${response.status} ${response.statusText}). ${errorBody}`
    );
  }

  return true;
};


export const getFolderAndFileList = async (orgId, userId) => {
  try {
    const stored = getStoredIds();
    const effectiveOrgId = orgId || stored.orgId;
    const effectiveUserId = userId || stored.userId;
    const token = getAuthToken();
    const url = `${API_URL}/file-upload/folder/${effectiveOrgId}/${effectiveUserId}`;
    const response = await axios.get(url, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `${token}` } : {}),
      },
    });
    if (handleSessionTimeout(response.status, response.data)) {
      return {
        success: false,
        data: [],
        message:
          response.data?.uiMsg || response.data?.message || "Session timeout",
      };
    }
    return response.data;
  } catch (error) {
    if (error?.response) {
      if (handleSessionTimeout(error.response.status, error.response.data)) {
        return {
          success: false,
          data: [],
          message:
            error.response.data?.uiMsg ||
            error.response.data?.message ||
            "Session timeout",
        };
      }
    }
    throw error;
  }
};

export const createNewFolder = async (
  parentFolderPath,
  folderName,
  orgId,
  userId
) => {
  try {
    const stored = getStoredIds();
    const effectiveOrgId = orgId || stored.orgId;
    const effectiveUserId = userId || stored.userId;
    const token = getAuthToken();
    const url = `${API_URL}/file-upload/folder/${effectiveOrgId}/${effectiveUserId}`;
    const response = await axios.post(
      url,
      { parentFolderPath, folderName },
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        }
      }
    );
    if (handleSessionTimeout(response.status, response.data)) {
      return {
        success: false,
        data: [],
        message:
          response.data?.uiMsg || response.data?.message || "Session timeout",
      };
    }
    return response.data;
  } catch (error) {
    if (error?.response) {
      if (handleSessionTimeout(error.response.status, error.response.data)) {
        return {
          success: false,
          data: [],
          message:
            error.response.data?.uiMsg ||
            error.response.data?.message ||
            "Session timeout",
        };
      }
    }
    throw error;
  }
};

export const reNameFolder = async (
  folderName,
  folderPath,
  orgId,
  userId
) => {
  try {
    const stored = getStoredIds();
    const effectiveOrgId = orgId || stored.orgId;
    const effectiveUserId = userId || stored.userId;
    const token = getAuthToken();
    const url = `${API_URL}/file-upload/folder/${effectiveOrgId}/${effectiveUserId}`;
    const response = await axios.patch(
      url,
      { folderName, folderPath },
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        }
      }
    );
    if (handleSessionTimeout(response.status, response.data)) {
      return {
        success: false,
        data: [],
        message:
          response.data?.uiMsg || response.data?.message || "Session timeout",
      };
    }
    return response.data;
  } catch (error) {
    if (error?.response) {
      if (handleSessionTimeout(error.response.status, error.response.data)) {
        return {
          success: false,
          data: [],
          message:
            error.response.data?.uiMsg ||
            error.response.data?.message ||
            "Session timeout",
        };
      }
    }
    throw error;
  }
};

export const removeFolder = async (
  folderPath,
  orgId,
  userId
) => {
  try {
    const stored = getStoredIds();
    const effectiveOrgId = orgId || stored.orgId;
    const effectiveUserId = userId || stored.userId;
    const url = `${API_URL}/file-upload/folder/${effectiveOrgId}/${effectiveUserId}`;
    const response = await axios.delete(url, {
      data: { folderPath },
      headers: { "Content-Type": "application/json" },
    });
    if (handleSessionTimeout(response.status, response.data)) {
      return {
        success: false,
        data: [],
        message:
          response.data?.uiMsg || response.data?.message || "Session timeout",
      };
    }
    return response.data;
  } catch (error) {
    if (error?.response) {
      if (handleSessionTimeout(error.response.status, error.response.data)) {
        return {
          success: false,
          data: [],
          message:
            error.response.data?.uiMsg ||
            error.response.data?.message ||
            "Session timeout",
        };
      }
    }
    throw error;
  }
};

export const removeFileFromS3Bucket = async (
  removeFilePath,
  orgId,
  userId
) => {
  try {
    const stored = getStoredIds();
    const effectiveOrgId = orgId || stored.orgId;
    const effectiveUserId = userId || stored.userId;
    const token = getAuthToken();
    const url = `${API_URL}/file-upload/${effectiveOrgId}/${effectiveUserId}`;
    const response = await axios.delete(url, {
      data: { removeFilePath },
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `${token}` } : {}),
      },
    });
    if (handleSessionTimeout(response.status, response.data)) {
      return {
        success: false,
        data: [],
        message:
          response.data?.uiMsg || response.data?.message || "Session timeout",
      };
    }
    return response.data;
  } catch (error) {
    if (error?.response) {
      if (handleSessionTimeout(error.response.status, error.response.data)) {
        return {
          success: false,
          data: [],
          message:
            error.response.data?.uiMsg ||
            error.response.data?.message ||
            "Session timeout",
        };
      }
    }
    throw error;
  }
};

export const reNameFile = async (
  filePath,
  fileName,
  orgId,
  userId
) => {
  try {
    const stored = getStoredIds();
    const effectiveOrgId = orgId || stored.orgId;
    const effectiveUserId = userId || stored.userId;
    const token = getAuthToken();
    const url = `${API_URL}/file-upload/${effectiveOrgId}/${effectiveUserId}`;
    const response = await axios.patch(
      url,
      { filePath, fileName },
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        }
      }
    );
    if (handleSessionTimeout(response.status, response.data)) {
      return {
        success: false,
        data: [],
        message:
          response.data?.uiMsg || response.data?.message || "Session timeout",
      };
    }
    return response.data;
  } catch (error) {
    if (error?.response) {
      if (handleSessionTimeout(error.response.status, error.response.data)) {
        return {
          success: false,
          data: [],
          message:
            error.response.data?.uiMsg ||
            error.response.data?.message ||
            "Session timeout",
        };
      }
    }
    throw error;
  }
};

export const getSingleFileDetails = async (
  s3Path,
  orgId,
  userId
) => {
  try {
    const stored = getStoredIds();
    const effectiveOrgId = orgId || stored.orgId;
    const effectiveUserId = userId || stored.userId;
    const token = getAuthToken();
    const url = `${API_URL}/file-upload/file-details/${effectiveOrgId}/${effectiveUserId}`;
    const response = await axios.post(
      url,
      { s3Path },
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `${token}` } : {}),
        }
      }
    );
    if (handleSessionTimeout(response.status, response.data)) {
      return {
        success: false,
        data: [],
        message:
          response.data?.uiMsg || response.data?.message || "Session timeout",
      };
    }
    return response.data;
  } catch (error) {
    if (error?.response) {
      if (handleSessionTimeout(error.response.status, error.response.data)) {
        return {
          success: false,
          data: [],
          message:
            error.response.data?.uiMsg ||
            error.response.data?.message ||
            "Session timeout",
        };
      }
    }
    throw error;
  }
};
